import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { captureUTMs, loadPixels, loadExperimentDefaults } from "@/lib/analytics";
import LandingPage from "@/pages/LandingPage";
import ServicesPage from "@/pages/ServicesPage";
import ContactPage from "@/pages/ContactPage";
import TrackingPage from "@/pages/TrackingPage";
import CarrierPublicPage from "@/pages/CarrierPublicPage";
import EtaEmbedPage from "@/pages/EtaEmbedPage";
import LegalPage from "@/pages/LegalPage";
import StatusPage from "@/pages/StatusPage";
import SmsOptInPage from "@/pages/SmsOptInPage";
import AdminLaunchPage from "@/pages/AdminLaunchPage";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import AppLayout from "@/pages/AppLayout";
import Dashboard from "@/pages/Dashboard";
import DispatchPage from "@/pages/DispatchPage";
import DriversPage from "@/pages/DriversPage";
import InvoicesPage from "@/pages/InvoicesPage";
import CustomerCreditPage from "@/pages/CustomerCreditPage";
import MaintenancePage from "@/pages/MaintenancePage";
import RecruitingPage from "@/pages/RecruitingPage";
import LeadsPage from "@/pages/LeadsPage";
import InboxPage from "@/pages/InboxPage";
import CalculatorPage from "@/pages/CalculatorPage";
import DriverPortal from "@/pages/DriverPortal";
import PricingPage from "@/pages/PricingPage";
import BookDemoPage from "@/pages/BookDemoPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import OwnerOpLandingPage from "@/pages/OwnerOpLandingPage";
import IndustryLandingPage from "@/pages/IndustryLandingPage";
import EnterprisePage from "@/pages/EnterprisePage";
import CustomersPage from "@/pages/CustomersPage";
import TeamPage from "@/pages/TeamPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import AuditLogPage from "@/pages/AuditLogPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";
import BillingPage from "@/pages/BillingPage";
import PricingPrefsPage from "@/pages/PricingPrefsPage";
import AdminPilotsPage from "@/pages/AdminPilotsPage";
import AdminCoachingResetsPage from "@/pages/AdminCoachingResetsPage";
import AdminSharingLeaderboardPage from "@/pages/AdminSharingLeaderboardPage";
import AdminTestimonialsPage from "@/pages/AdminTestimonialsPage";
import AdminFounderLetterPage from "@/pages/AdminFounderLetterPage";
import AdminReplyTemplatesPage from "@/pages/AdminReplyTemplatesPage";
import AdminWeeklyCohortsPage from "@/pages/AdminWeeklyCohortsPage";
import TestimonialRequestPage from "@/pages/TestimonialRequestPage";
import RefRedirectPage from "@/pages/RefRedirectPage";
import AccountReferralPage from "@/pages/AccountReferralPage";
import ReferralLeaderboardPage from "@/pages/ReferralLeaderboardPage";
import MetaAdLandingPage from "@/pages/MetaAdLandingPage";
import TikTokLandingPage from "@/pages/TikTokLandingPage";
import ReelsLandingPage from "@/pages/ReelsLandingPage";
import WatchReelsPage from "@/pages/WatchReelsPage";
import FacebookAdLandingPage from "@/pages/FacebookAdLandingPage";
import ShortsLandingPage from "@/pages/ShortsLandingPage";
import AdminMetricsPage from "@/pages/AdminMetricsPage";
import AdminWinBackPage from "@/pages/AdminWinBackPage";
import AdminLeadsPage from "@/pages/AdminLeadsPage";
import AdminActivationPage from "@/pages/AdminActivationPage";
import AdminAdStudioPage from "@/pages/AdminAdStudioPage";
import AdminExperimentsPage from "@/pages/AdminExperimentsPage";
import AdminIntegrationsPage from "@/pages/AdminIntegrationsPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import AdminNurturePage from "@/pages/AdminNurturePage";
import FounderDashboardPage from "@/pages/FounderDashboardPage";
import AdminPilotCohortPage from "@/pages/AdminPilotCohortPage";
import AdminPilotDripPage from "@/pages/AdminPilotDripPage";
import AdminFreeWorkflowPage from "@/pages/AdminFreeWorkflowPage";
import AdminSuppressionsPage from "@/pages/AdminSuppressionsPage";
import AdminSmsSuppressionsPage from "@/pages/AdminSmsSuppressionsPage";
import AdminWaitlistPage from "@/pages/AdminWaitlistPage";
import FlyerPage from "@/pages/FlyerPage";
import AltexAuditPage from "@/pages/AltexAuditPage";
import TikTok30DayChallengePage from "@/pages/TikTok30DayChallengePage";
import BrokerCheckPage from "@/pages/BrokerCheckPage";
import RateCheckPage from "@/pages/RateCheckPage";
import ToolsHubPage from "@/pages/ToolsHubPage";
import AuthorityChecklistPage from "@/pages/AuthorityChecklistPage";
import ShortLinkMC from "@/pages/ShortLinkMC";
import PartnerGoPage from "@/pages/PartnerGoPage";
import PartnersPitchPage from "@/pages/PartnersPitchPage";
import WinPage from "@/pages/WinPage";
import PartnerStatsPage from "@/pages/PartnerStatsPage";
import AdminPartnersPage from "@/pages/AdminPartnersPage";
import DNPPage from "@/pages/DNPPage";
import CareersBuilderPage from "@/pages/CareersBuilderPage";
import CareersEditPage from "@/pages/CareersEditPage";
import LoadFilterPrivacyPage from "@/pages/LoadFilterPrivacyPage";
import FounderPitchPage from "@/pages/FounderPitchPage";
import LeaderboardCarriersPage from "@/pages/LeaderboardCarriersPage";
import PublicCareersPage from "@/pages/PublicCareersPage";
import AdminFeedbackPage from "@/pages/AdminFeedbackPage";
import AdminSignupReviewPage from "@/pages/AdminSignupReviewPage";
import AdminDNPPage from "@/pages/AdminDNPPage";
import PlaybookPage from "@/pages/PlaybookPage";
import AdminTikTokPostsPage from "@/pages/AdminTikTokPostsPage";
import TikTokReelsPage from "@/pages/TikTokReelsPage";
import RoadmapPage from "@/pages/RoadmapPage";
import BusinessOverviewPage from "@/pages/BusinessOverviewPage";
import PostLoadPage from "@/pages/PostLoadPage";
import WeeklyMilesReportPage from "@/pages/WeeklyMilesReportPage";
import StateMileageReportPage from "@/pages/StateMileageReportPage";
import LaneProfitabilityPage from "@/pages/LaneProfitabilityPage";
import BrokerRateCardPage from "@/pages/BrokerRateCardPage";
import AdminDigestPage from "@/pages/AdminDigestPage";
import AdminDigestSnapshotPage from "@/pages/AdminDigestSnapshotPage";
import AdminManualPaymentsPage from "@/pages/AdminManualPaymentsPage";
import PaperworkPage from "@/pages/PaperworkPage";
import PayrollPage from "@/pages/PayrollPage";
import WalkthroughPage from "@/pages/WalkthroughPage";
import DriverPayPortal from "@/pages/DriverPayPortal";
import LoadBoardPage from "@/pages/LoadBoardPage";
import LoadBoardAnalyticsPage from "@/pages/LoadBoardAnalyticsPage";
import AlertsPage from "@/pages/AlertsPage";
import AccountSettingsPage from "@/pages/AccountSettingsPage";
import DocsVaultPage from "@/pages/DocsVaultPage";
import AdminRosterPage from "@/pages/AdminRosterPage";
import AdminInviteAcceptPage from "@/pages/AdminInviteAcceptPage";
import VAOnboardingPage from "@/pages/VAOnboardingPage";
import VAActivityPage from "@/pages/VAActivityPage";
import VABonusPage from "@/pages/VABonusPage";
import CookieConsent from "@/components/CookieConsent";
import { LeadChatWidget } from "@/components/LeadChatWidget";

const CHAT_ROUTES = ["/", "/owner-ops", "/lp/small-fleet", "/lp/facebook", "/tiktok", "/reels", "/shorts", "/watch", "/pricing"];

function PublicChat() {
  const { pathname } = useLocation();
  const show = CHAT_ROUTES.includes(pathname) || pathname.startsWith("/for/");
  return show ? <LeadChatWidget /> : null;
}
import InstallPrompt from "@/components/InstallPrompt";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-neutral-500 text-sm">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    // Fresh signups land on the First Week playbook (or billing when they
    // arrived with a chosen plan). Read-only here — StrictMode double-renders
    // guards, so the flag is cleared by PlaybookPage on mount, never here.
    let fresh = null;
    try { fresh = sessionStorage.getItem("fp_fresh_signup"); } catch {}
    if (fresh) {
      let plan = new URLSearchParams(window.location.search).get("plan");
      if (!plan) {
        try { plan = sessionStorage.getItem("chosen_plan"); } catch {}
      }
      if (plan) {
        try { sessionStorage.removeItem("fp_fresh_signup"); } catch {}
        return <Navigate to={`/app/billing?plan=${plan}`} replace />;
      }
      return <Navigate to="/app/playbook" replace />;
    }
    return <Navigate to="/app/dashboard" replace />;
  }
  return children;
}

export default function App() {
  useEffect(() => { captureUTMs(); loadPixels(); loadExperimentDefaults(); }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/owner-ops" element={<OwnerOpLandingPage />} />
            <Route path="/enterprise" element={<EnterprisePage />} />
            <Route path="/customers" element={<CustomersPage />} />
            <Route path="/testimonial/:token" element={<TestimonialRequestPage />} />
            <Route path="/r/:code" element={<RefRedirectPage />} />
            <Route path="/referrals/leaderboard" element={<ReferralLeaderboardPage />} />
            <Route path="/lp/small-fleet" element={<MetaAdLandingPage />} />
            <Route path="/lp/facebook" element={<FacebookAdLandingPage />} />
            <Route path="/tiktok" element={<TikTokLandingPage />} />
            <Route path="/reels" element={<ReelsLandingPage />} />
            <Route path="/watch" element={<WatchReelsPage />} />
            <Route path="/shorts" element={<ShortsLandingPage />} />
            <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
            <Route path="/admin-invite/:token" element={<AdminInviteAcceptPage />} />
            <Route path="/for/:slug" element={<IndustryLandingPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/demo" element={<BookDemoPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/roadmap" element={<RoadmapPage />} />
            <Route path="/business-overview" element={<BusinessOverviewPage />} />
            <Route path="/post-load" element={<PostLoadPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/founding-pilot" element={<Navigate to="/pricing" replace />} />
            <Route path="/flyer" element={<FlyerPage />} />
            <Route path="/audit-altex" element={<AltexAuditPage />} />
            <Route path="/tiktok-30day" element={<TikTok30DayChallengePage />} />
            <Route path="/broker-check" element={<BrokerCheckPage />} />
            <Route path="/rate-check" element={<RateCheckPage />} />
            <Route path="/tools" element={<ToolsHubPage />} />
            <Route path="/authority-checklist" element={<AuthorityChecklistPage />} />
            <Route path="/mc" element={<ShortLinkMC />} />
            <Route path="/go/:code" element={<PartnerGoPage />} />
            <Route path="/partners" element={<PartnersPitchPage />} />
            <Route path="/win/:token" element={<WinPage />} />
            <Route path="/partner/:code" element={<PartnerStatsPage />} />
            <Route path="/dnp" element={<DNPPage />} />
            <Route path="/tiktoks" element={<TikTokReelsPage />} />
            <Route path="/careers-builder" element={<CareersBuilderPage />} />
            <Route path="/careers-edit" element={<CareersEditPage />} />
            <Route path="/loadfilter-privacy" element={<LoadFilterPrivacyPage />} />
            <Route path="/founder-pitch" element={<FounderPitchPage />} />
            <Route path="/leaderboard/carriers" element={<LeaderboardCarriersPage />} />
            <Route path="/jobs/:slug" element={<PublicCareersPage />} />
            <Route path="/track/:loadId" element={<TrackingPage />} />
            <Route path="/carriers/:slug" element={<CarrierPublicPage />} />
            <Route path="/embed/eta/:loadId" element={<EtaEmbedPage />} />
            <Route path="/driver/:token" element={<DriverPortal />} />
            <Route path="/pay" element={<DriverPayPortal />} />
            <Route path="/pay/verify" element={<DriverPayPortal />} />
            <Route path="/privacy" element={<LegalPage type="privacy" />} />
            <Route path="/terms" element={<LegalPage type="terms" />} />
            <Route path="/status" element={<StatusPage />} />
            <Route path="/sms-opt-in" element={<SmsOptInPage />} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/payment/cancel" element={<PaymentCancelPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/login" element={<PublicOnly><AuthPage mode="login" /></PublicOnly>} />
            <Route path="/signup" element={<PublicOnly><AuthPage mode="signup" /></PublicOnly>} />
            {/* Ad landing: signup form first, then straight to the Stripe card page (no plan picking) */}
            <Route path="/start" element={<AuthPage mode="signup" direct />} />
            <Route path="/app" element={<Protected><AppLayout /></Protected>}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="playbook" element={<PlaybookPage />} />
              <Route path="authority" element={<AuthorityChecklistPage embedded />} />
              <Route path="dot-check" element={<BrokerCheckPage embedded />} />
              <Route path="dispatch" element={<DispatchPage />} />
              <Route path="loadboard" element={<LoadBoardPage />} />
              <Route path="loadboard/analytics" element={<LoadBoardAnalyticsPage />} />
              <Route path="alerts" element={<AlertsPage />} />
              <Route path="drivers" element={<DriversPage />} />
              <Route path="payroll" element={<PayrollPage />} />
              <Route path="walkthrough/:feature" element={<WalkthroughPage />} />
              <Route path="reports/miles" element={<WeeklyMilesReportPage />} />
              <Route path="reports/state-mileage" element={<StateMileageReportPage />} />
              <Route path="reports/lanes" element={<LaneProfitabilityPage />} />
              <Route path="broker-rate-card" element={<BrokerRateCardPage />} />
              <Route path="paperwork" element={<PaperworkPage />} />
              <Route path="vault" element={<DocsVaultPage />} />
              <Route path="invoices" element={<InvoicesPage />} />
              <Route path="customer-credit" element={<CustomerCreditPage />} />
              <Route path="maintenance" element={<MaintenancePage />} />
              <Route path="recruiting" element={<RecruitingPage />} />
              <Route path="leads" element={<LeadsPage />} />
              <Route path="calculator" element={<CalculatorPage />} />
              <Route path="inbox" element={<InboxPage />} />
              <Route path="billing" element={<BillingPage />} />
              <Route path="team" element={<TeamPage />} />
              <Route path="audit" element={<AuditLogPage />} />
              <Route path="referrals" element={<AccountReferralPage />} />
              <Route path="settings/pricing" element={<PricingPrefsPage />} />
              <Route path="account/settings" element={<AccountSettingsPage />} />
              <Route path="admin/roster" element={<AdminRosterPage />} />
              <Route path="admin/va-onboarding" element={<VAOnboardingPage />} />
              <Route path="admin/va-activity" element={<VAActivityPage />} />
              <Route path="admin/va-bonus" element={<VABonusPage />} />
              <Route path="admin/pilots" element={<AdminPilotsPage />} />
              <Route path="admin/coaching-resets" element={<AdminCoachingResetsPage />} />
              <Route path="admin/sharing-leaderboard" element={<AdminSharingLeaderboardPage />} />
              <Route path="admin/testimonials" element={<AdminTestimonialsPage />} />
              <Route path="admin/partners" element={<AdminPartnersPage />} />
              <Route path="admin/founder-letter" element={<AdminFounderLetterPage />} />
              <Route path="admin/reply-templates" element={<AdminReplyTemplatesPage />} />
              <Route path="admin/weekly-cohorts" element={<AdminWeeklyCohortsPage />} />
              <Route path="admin/metrics" element={<AdminMetricsPage />} />
              <Route path="admin/launch" element={<AdminLaunchPage />} />
              <Route path="admin/win-back" element={<AdminWinBackPage />} />
              <Route path="admin/leads" element={<AdminLeadsPage />} />
              <Route path="admin/activation" element={<AdminActivationPage />} />
              <Route path="admin/experiments" element={<AdminExperimentsPage />} />
              <Route path="admin/ad-studio" element={<AdminAdStudioPage />} />
              <Route path="admin/integrations" element={<AdminIntegrationsPage />} />
              <Route path="integrations" element={<IntegrationsPage />} />
              <Route path="admin/nurture" element={<AdminNurturePage />} />
              <Route path="admin/founder-dashboard" element={<FounderDashboardPage />} />
              <Route path="admin/pilot-cohort" element={<AdminPilotCohortPage />} />
              <Route path="admin/pilot-drip" element={<AdminPilotDripPage />} />
              <Route path="admin/free-workflow" element={<AdminFreeWorkflowPage />} />
              <Route path="admin/suppressions" element={<AdminSuppressionsPage />} />
              <Route path="admin/sms-suppressions" element={<AdminSmsSuppressionsPage />} />
              <Route path="admin/waitlist" element={<AdminWaitlistPage />} />
              <Route path="admin/feedback" element={<AdminFeedbackPage />} />
              <Route path="admin/signup-review" element={<AdminSignupReviewPage />} />
              <Route path="admin/dnp" element={<AdminDNPPage />} />
              <Route path="admin/tiktoks" element={<AdminTikTokPostsPage />} />
              <Route path="admin/digest" element={<AdminDigestPage />} />
              <Route path="admin/digest-snapshots" element={<AdminDigestSnapshotPage />} />
              <Route path="admin/manual-payments" element={<AdminManualPaymentsPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster theme="dark" position="top-right" />
          <CookieConsent />
          <PublicChat />
          <InstallPrompt />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
